/**
 * RowActionToolbar — context-aware procedure CTAs that live on each
 * FindingsSummary row inside the tooth chart.
 *
 * Buttons shown depend on the sitting's current status (the full procedure
 * list is opened by clicking the row's PLAN line, not a button here):
 *
 *   planned / scheduled / awaiting_payment / payment_cleared / held / ready
 *     → ▶ Start  + 📝 Note  + ⋯ (Cancel)
 *
 *   in_progress
 *     → ✓ Complete  + 📝 Note  + ⋯ (Cancel)
 *
 *   pending_approval / completed / paid
 *     → 📝 Note
 *
 *   cancelled / rejected / voided
 *     → no buttons (the row's STATUS chip says "Cancelled"; nothing to do)
 *
 * Every action follows the same shape:
 *   1. Set busy + push an optimistic sitting row into the store so the
 *      chip strip + chart paint the new state within one render.
 *   2. Fire the API call (executeSitting / completeSitting / cancelSitting
 *      / addProcedureNote).
 *   3. Call useDentalHistoryStore.refresh(patientId) so the bundle is
 *      re-fetched and both surfaces converge on server truth.
 *   4. On failure: surface a small inline alert + re-refresh to drop
 *      the optimistic row.
 *
 * The toolbar takes its callbacks from props rather than reaching into
 * the store directly — keeps the component shallow-testable.
 */
import React, { useState } from 'react';

import {
  cancelSitting,
  completeSitting,
  executeSitting,
  extendExpirySitting,
} from '../../../../services/api/patientPlanApi';
import { addProcedureNote } from '../../../../services/api/visitMetadataApi';
import eventBus from '../../../../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../../../../services/realtime/RealtimeSyncService';
import useSittingStore from '../../../../stores/sittingStore';


const PLANNED_LIKE = new Set([
  'planned', 'scheduled', 'awaiting_payment', 'payment_cleared',
  'held', 'ready', 'approved',
]);
const TERMINAL = new Set(['completed', 'paid', 'cancelled', 'voided', 'rejected', 'skipped']);


/**
 * Pick the primary CTA the dentist should see for this status. Returns
 * null when no primary action is appropriate (terminal cancelled / voided).
 */
function primaryActionFor(status) {
  const s = String(status || '').toLowerCase();
  if (PLANNED_LIKE.has(s)) return 'start';
  if (s === 'in_progress') return 'complete';
  if (s === 'pending_approval') return 'view';
  if (s === 'completed' || s === 'paid') return 'view';
  return null;
}

// The patientPlanApi / visitMetadataApi wrappers DON'T throw on HTTP errors —
// they return `{ success: false, error }`. So a failed call must be detected by
// inspecting the result, or it silently "succeeds" (emits events, reverts on
// refresh) with no error shown. These read that contract.
function apiFailed(res) {
  return !!res && res.success === false;
}
function apiErrMsg(res, fallback) {
  const e = res?.error;
  if (e && typeof e === 'object' && e.message) return e.message;
  if (typeof e === 'string' && e) return e;
  return fallback;
}


export default function RowActionToolbar({
  plan,                  // item.plan from the projection — { sittingId, planId, visitId, status, name, ... }
  patientId,             // for the optimistic-store handler
  addOptimisticSitting,  // from usePatientToothHistory()
  onRefresh,             // bound refresh(patientId) from usePatientToothHistory()
  isDark = false,
  // Start/Complete are backend-enforced to the assignee. In a patient-wide list
  // (the generic Procedure Summary) a procedure may belong to another doctor, so
  // the host passes false to hide those CTAs (Note/Cancel/Extend stay). Defaults
  // true so the dental chart is unchanged.
  canStartComplete = true,
}) {
  const [busy, setBusy] = useState(null);   // 'start' | 'complete' | 'note' | 'cancel' | 'extend'
  const [error, setError] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (!plan || !plan.sittingId) return null;

  const status = String(plan.status || '').toLowerCase();
  const primary = primaryActionFor(status);
  if (!primary && TERMINAL.has(status)) return null;

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const btnBase = [
    'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold',
    'border min-h-[28px] transition',
    'disabled:opacity-60 disabled:cursor-not-allowed',
  ].join(' ');

  // Canonical outline palette — used by View, Note, and (with a
  // destructive hover tint) Cancel. Per CLAUDE.md button-visibility
  // rule: solid bg in both modes + border weight ≥ -500 so the button
  // reads against the chart's tinted card without a hover lookup.
  const outlineBtn = isDark
    ? 'bg-slate-800 border-slate-500 text-slate-100 hover:bg-slate-700'
    : 'bg-white border-slate-500 text-slate-800 hover:bg-slate-100';
  const cancelBtn = isDark
    ? 'bg-slate-800 border-slate-500 text-slate-200 hover:bg-rose-900/40 hover:text-rose-200'
    : 'bg-white border-slate-500 text-slate-700 hover:bg-rose-50 hover:text-rose-700';

  /**
   * After every successful action, emit a local eventBus event matching
   * the canonical WS event name. Both `dentalHistoryStore` (chart + chip
   * strip) AND `sittingStore` (Procedures widget) subscribe — so this
   * single emit propagates the change to every consumer in one render,
   * without depending on the WebSocket round-trip.
   *
   * NB the EventBus contract is `emit({ type, payload, source? })` —
   * NOT `emit(name, payload)`. Source is intentionally omitted so the
   * realtime middleware treats this as a remote-equivalent event (its
   * "skip if source === local" branch otherwise skips our handlers).
   */
  const emitSittingEvent = (eventName, status) => {
    try {
      eventBus.emit({
        type: eventName,
        payload: {
          sitting_id: plan.sittingId,
          visit_id: plan.visitId,
          patient_id: patientId,
          status,
        },
      });
    } catch (_e) { /* SSR / handler errors don't break the API success */ }
  };

  const handleStart = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    setBusy('start');
    setError(null);
    try {
      addOptimisticSitting?.({ sitting_id: plan.sittingId, status: 'in_progress' });
      const res = await executeSitting(plan.planId, plan.sittingId);
      if (apiFailed(res)) {
        setError(apiErrMsg(res, 'Could not start'));
        await onRefresh?.(); // roll back the optimistic in_progress
        return;
      }
      // K3 — broadcast locally so every store (dentalHistoryStore +
      // sittingStore + future listeners) updates in one render.
      emitSittingEvent(REALTIME_EVENTS.SITTING_EXECUTION_STARTED, 'in_progress');
      // K4 — defensive belt: also kick a per-visit refetch on the
      // Procedures widget's store. Cheap (request coalescing dedupes).
      if (plan.visitId) {
        useSittingStore.getState().fetchForVisit?.(plan.visitId, { force: true });
      }
      await onRefresh?.();
    } catch (err) {
      setError(err?.message || 'Could not start');
      await onRefresh?.();
    } finally {
      setBusy(null);
    }
  };

  const handleComplete = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    setBusy('complete');
    setError(null);
    try {
      addOptimisticSitting?.({ sitting_id: plan.sittingId, status: 'completed' });
      const res = await completeSitting(plan.planId, plan.sittingId, {});
      if (apiFailed(res)) {
        setError(apiErrMsg(res, 'Could not complete'));
        await onRefresh?.(); // roll back the optimistic completed
        return;
      }
      emitSittingEvent(REALTIME_EVENTS.SITTING_COMPLETED, 'completed');
      if (plan.visitId) {
        useSittingStore.getState().fetchForVisit?.(plan.visitId, { force: true });
      }
      await onRefresh?.();
    } catch (err) {
      setError(err?.message || 'Could not complete');
      await onRefresh?.();
    } finally {
      setBusy(null);
    }
  };

  const handleSaveNote = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    const content = (noteText || '').trim();
    if (!content) return;
    if (!plan.visitId) {
      setError('Cannot attach note — visit context is missing.');
      return;
    }
    setBusy('note');
    setError(null);
    try {
      // Note: visitMetadataApi.addProcedureNote THROWS on error (unlike the
      // patientPlanApi sitting wrappers), so the catch below already surfaces it.
      await addProcedureNote(plan.visitId, {
        content,
        linked_sitting_id: plan.sittingId,
      });
      setNoteText('');
      setNoteOpen(false);
      // K3 — broadcast so the Procedures widget shows the new note
      // attachment without waiting for the WS event.
      emitSittingEvent(REALTIME_EVENTS.SITTING_NOTE_ADDED, plan.status);
      await onRefresh?.();
    } catch (err) {
      setError(err?.message || 'Could not save note');
    } finally {
      setBusy(null);
    }
  };

  // Inline confirm (no window.prompt — it's a no-op inside the VSCode webview,
  // which silently swallowed the Cancel click). Reason is optional; min 3 chars
  // if provided. The reason lives in `cancelReason`, captured by the inline panel.
  const handleCancel = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    const trimmed = (cancelReason || '').trim();
    if (trimmed && trimmed.length < 3) {
      setError('Reason must be at least 3 characters');
      return;
    }
    setBusy('cancel');
    setError(null);
    try {
      addOptimisticSitting?.({ sitting_id: plan.sittingId, status: 'cancelled' });
      const res = await cancelSitting(plan.planId, plan.sittingId, {
        reason: trimmed || 'Cancelled from chart',
      });
      if (apiFailed(res)) {
        setError(apiErrMsg(res, 'Could not cancel'));
        await onRefresh?.(); // roll back the optimistic cancelled; keep panel open
        return;
      }
      emitSittingEvent(REALTIME_EVENTS.SITTING_CANCELLED, 'cancelled');
      if (plan.visitId) {
        useSittingStore.getState().fetchForVisit?.(plan.visitId, { force: true });
      }
      setCancelOpen(false);
      setCancelReason('');
      await onRefresh?.();
    } catch (err) {
      setError(err?.message || 'Could not cancel');
      await onRefresh?.();
    } finally {
      setBusy(null);
    }
  };

  const handleExtend = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    const date = (extendDate || '').slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    if (!date || date < today) {
      setError('Pick a date that is today or later');
      return;
    }
    setBusy('extend');
    setError(null);
    try {
      const res = await extendExpirySitting(plan.planId, plan.sittingId, { expires_at: date });
      if (apiFailed(res)) {
        setError(apiErrMsg(res, 'Could not extend expiry')); // keep panel open
        return;
      }
      emitSittingEvent(REALTIME_EVENTS.SITTING_SCHEDULED, plan.status);
      if (plan.visitId) {
        useSittingStore.getState().fetchForVisit?.(plan.visitId, { force: true });
      }
      await onRefresh?.();
      setExtendOpen(false);
    } catch (err) {
      setError(err?.message || 'Could not extend expiry');
    } finally {
      setBusy(null);
    }
  };

  // ── Buttons ────────────────────────────────────────────────────
  const ExtendBtn = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setExtendDate(plan.expiresAt ? String(plan.expiresAt).slice(0, 10) : '');
        setExtendOpen((v) => !v);
        setError(null);
      }}
      disabled={!!busy || !plan.planId}
      aria-expanded={extendOpen}
      aria-label={`Extend expiry for ${plan.name || 'procedure'}`}
      className={[btnBase, outlineBtn].join(' ')}
      data-testid={`row-action-extend-${plan.sittingId}`}
    >
      ⏲ Extend
    </button>
  );

  const StartBtn = (
    <button
      type="button"
      onClick={handleStart}
      disabled={!!busy || !plan.planId}
      aria-busy={busy === 'start'}
      aria-label={`Start procedure ${plan.name || ''}`}
      title={status === 'awaiting_payment'
        ? 'Needs payment — collect & start from the Procedures panel'
        : 'For payment & queue, use the Procedures panel'}
      className={[btnBase,
        isDark
          ? 'bg-blue-700 border-blue-500 text-white hover:bg-blue-600'
          : 'bg-blue-600 border-blue-700 text-white hover:bg-blue-700',
      ].join(' ')}
      data-testid={`row-action-start-${plan.sittingId}`}
    >
      <span aria-hidden="true">▶</span> Start
    </button>
  );

  const CompleteBtn = (
    <button
      type="button"
      onClick={handleComplete}
      disabled={!!busy || !plan.planId}
      aria-busy={busy === 'complete'}
      aria-label={`Complete procedure ${plan.name || ''}`}
      className={[btnBase,
        isDark
          ? 'bg-emerald-700 border-emerald-500 text-white hover:bg-emerald-600'
          : 'bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700',
      ].join(' ')}
      data-testid={`row-action-complete-${plan.sittingId}`}
    >
      <span aria-hidden="true">✓</span> Complete
    </button>
  );

  const NoteBtn = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setNoteOpen((v) => !v); setError(null); }}
      disabled={!!busy}
      aria-expanded={noteOpen}
      aria-label={`Add note to ${plan.name || 'procedure'}`}
      className={[btnBase, outlineBtn].join(' ')}
      data-testid={`row-action-note-${plan.sittingId}`}
    >
      📝 Note
    </button>
  );

  const CancelBtn = (status !== 'completed' && status !== 'paid') ? (
    <button
      type="button"
      onClick={(ev) => {
        ev.stopPropagation();
        setError(null);
        setCancelReason('');
        setCancelOpen((v) => !v);
      }}
      disabled={!!busy || !plan.planId}
      aria-busy={busy === 'cancel'}
      aria-expanded={cancelOpen}
      aria-label={`Cancel procedure ${plan.name || ''}`}
      className={[btnBase, cancelBtn].join(' ')}
      data-testid={`row-action-cancel-${plan.sittingId}`}
    >
      × Cancel
    </button>
  ) : null;

  return (
    <div
      className="inline-flex items-start gap-1.5 flex-wrap"
      data-testid={`row-action-toolbar-${plan.sittingId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="inline-flex items-center gap-1.5 flex-wrap">
        {primary === 'start' && canStartComplete && StartBtn}
        {primary === 'complete' && canStartComplete && CompleteBtn}
        {NoteBtn}
        {/* Extend validity — only for not-yet-started (planned/awaiting) work. */}
        {primary === 'start' && ExtendBtn}
        {CancelBtn}
      </div>

      {/* F3 — Start can't proceed until payment clears; point the doctor to the
          Procedures panel where they collect payment & manage the queue. */}
      {primary === 'start' && status === 'awaiting_payment' && (
        <div className={['w-full mt-1 text-[11px]', muted].join(' ')}>
          Collect payment &amp; start from the <strong>Procedures</strong> panel →
        </div>
      )}

      {cancelOpen && (
        <div
          className="w-full mt-1.5 flex items-center gap-1.5 flex-wrap"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason (optional)"
            aria-label="Cancellation reason"
            className={[
              'flex-1 min-w-[8rem] px-2 py-1 text-[12px] rounded-md border',
              isDark
                ? 'bg-slate-800 border-slate-600 text-slate-100'
                : 'bg-white border-slate-400 text-slate-900',
            ].join(' ')}
          />
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy === 'cancel'}
            aria-busy={busy === 'cancel'}
            className={[btnBase,
              isDark
                ? 'bg-rose-700 border-rose-500 text-white hover:bg-rose-600'
                : 'bg-rose-600 border-rose-700 text-white hover:bg-rose-700',
            ].join(' ')}
            data-testid={`row-action-cancel-confirm-${plan.sittingId}`}
          >
            {busy === 'cancel' ? 'Cancelling…' : 'Confirm cancel'}
          </button>
          <button
            type="button"
            onClick={() => { setCancelOpen(false); setCancelReason(''); }}
            disabled={busy === 'cancel'}
            className={[btnBase,
              isDark
                ? 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                : 'bg-white border-slate-400 text-slate-800 hover:bg-slate-100',
            ].join(' ')}
          >
            Back
          </button>
        </div>
      )}

      {extendOpen && (
        <div
          className="w-full mt-1.5 flex items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="date"
            value={extendDate ? String(extendDate).slice(0, 10) : ''}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setExtendDate(e.target.value)}
            aria-label="New validity date"
            className={[
              'px-2 py-1 text-[12px] rounded-md border',
              isDark
                ? 'bg-slate-800 border-slate-600 text-slate-100'
                : 'bg-white border-slate-400 text-slate-900',
            ].join(' ')}
          />
          <button
            type="button"
            onClick={handleExtend}
            disabled={busy === 'extend' || !extendDate}
            aria-busy={busy === 'extend'}
            className={[btnBase,
              isDark
                ? 'bg-teal-700 border-teal-500 text-white hover:bg-teal-600'
                : 'bg-teal-600 border-teal-700 text-white hover:bg-teal-700',
            ].join(' ')}
            data-testid={`row-action-extend-save-${plan.sittingId}`}
          >
            Save
          </button>
        </div>
      )}

      {noteOpen && (
        <div className="w-full mt-1.5 flex items-stretch gap-1.5">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={2}
            maxLength={2000}
            autoFocus
            placeholder={`Note about ${plan.name || 'this procedure'}…`}
            className={[
              'flex-1 px-2 py-1 text-[12px] rounded-md border resize-y',
              isDark
                ? 'bg-slate-800 border-slate-600 text-slate-100'
                : 'bg-white border-slate-400 text-slate-900',
            ].join(' ')}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={handleSaveNote}
            disabled={busy === 'note' || !noteText.trim()}
            aria-busy={busy === 'note'}
            className={[btnBase, 'self-start',
              isDark
                ? 'bg-teal-700 border-teal-500 text-white hover:bg-teal-600'
                : 'bg-teal-600 border-teal-700 text-white hover:bg-teal-700',
            ].join(' ')}
            data-testid={`row-action-note-save-${plan.sittingId}`}
          >
            Save
          </button>
        </div>
      )}

      {error && (
        <div className="w-full mt-1" role="alert">
          <span className={[
            'text-[11px]',
            isDark ? 'text-rose-300' : 'text-rose-600',
          ].join(' ')}>
            {error}
          </span>
        </div>
      )}

      {/* aria-live region so screen readers announce success silently */}
      <span className={['sr-only', muted].join(' ')} aria-live="polite">
        {busy ? `${busy} in progress` : ''}
      </span>
    </div>
  );
}
